import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { ErrorComponent } from './core/error-handling/error/error.component';
import { AuthGuard } from './core/guards/auth.guard';
import { AuthCallbackGuard } from './core/guards/authcallback.guard';
import { CanDeactivateGuard } from './core/guards/can-deactivate.guard';
import { MobileSigneringGuard } from './core/guards/mobile-signering-guard';
import { SilentAuthGuard } from './core/guards/silentauth.guard';
import { KvitteringRedirectComponent } from './core/pages/kvittering-redirect/kvittering-redirect.component';
import { PageNotFoundComponent } from './core/pages/page-not-found/page-not-found.component';
import { DigitalSigneringKvitteringComponent } from './pages/digital-signering-kvittering/digital-signering-kvittering.component';
import { DigitalSigneringMitidMobileComponent } from './pages/digital-signering-mitid-mobile/digital-signering-mitid-mobile.component';
import { DigitalSigneringMitidComponent } from './pages/digital-signering-mitid/digital-signering-mitid.component';
import { DigitalsigneringMobileComponent } from './pages/digital-signering-mobile/digital-signering-mobile.component';
import { DigitalSigneringComponent } from './pages/digital-signering/digital-signering.component';
import { FrontPageComponent } from './pages/front-page/front-page.component';
import { LoggedOutPageComponent } from './pages/logged-out-page/logged-out-page.component';
import { SagRedirectComponent } from './pages/sag-redirect/sag-redirect.component';
import { SkiftBrugerComponent } from './pages/skift-bruger/skift-bruger.component';
import { RedirectWithQueryParamGuard } from './projekter/guards/redirect-with-query-param.guard';
import { SagRedirectGuard } from './projekter/guards/sag-redirect-guard';

const routes: Routes = [
  { path: '', component: FrontPageComponent, pathMatch: 'full', canActivate: [SilentAuthGuard] },
  {
    path: 'projekter',
    loadChildren: () => import('./projekter/projekter.module').then(m => m.ProjekterModule),
  },
  {
    path: 'profil',
    loadChildren: () => import('./profil/profil.module').then(m => m.ProfilModule),
  },
  {
    path: 'hjaelp',
    loadChildren: () => import('./hjaelp/hjaelp.module').then(m => m.HjaelpModule),
    canActivate: [SilentAuthGuard],
  },
  {
    path: 'virksomhed',
    loadChildren: () => import('./virksomhed/virksomhed.module').then(m => m.VirksomhedModule),
  },
  // Needed to support project invitation URLs from PublicWeb mails
  {
    path: 'Rettighed/AccepterInvitation',
    component: PageNotFoundComponent, // Must have a component here but RedirectWithQueryParamGuard will redirect to AccepterInvitation
    canActivate: [RedirectWithQueryParamGuard],
    data: { redirectPathSegment: '/projekter/accepterinvitation', queryParamName: 'ticket' },
  },
  {
    path: 'Signering/index',
    component: PageNotFoundComponent, // Must have a component here but RedirectWithQueryParamGuard will redirect to digitalsignering
    canActivate: [RedirectWithQueryParamGuard],
    data: { redirectPathSegment: '/digitalsignering', queryParamName: 'ticket' },
  },
  {
    path: 'Sag/Kvittering/:ansoegningId',
    component: KvitteringRedirectComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'Sag/Vis/:sagId/dokumentation/:dokId',
    component: SagRedirectComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'Sag/Vis/:sagId', // Needed to support sag URLs from PublicWeb mails
    component: PageNotFoundComponent, // Must have a component here but SagRedirectGuard will redirect to SagRedirectComponent
    canActivate: [SagRedirectGuard],
  },
  {
    path: 'sag/:sagId/:dokId',
    component: SagRedirectComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'sag/:sagId',
    component: SagRedirectComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'digitalsignering/kvittering',
    component: DigitalSigneringKvitteringComponent,
  },
  {
    path: 'digitalsignering/:ticket',
    component: DigitalSigneringComponent,
    canActivate: [MobileSigneringGuard],
  },
  { path: 'digitalsignering/:ticket/mitid', component: DigitalSigneringMitidComponent },
  { path: 'digitalsigneringmobile/:ticket', component: DigitalsigneringMobileComponent },
  { path: 'digitalsigneringmobile/:ticket/mitid', component: DigitalSigneringMitidMobileComponent },
  // We use a guard to handle postsigninredirect, to avoid errors in IE when large urls
  // containing token are passed to the angular router
  { path: 'callback', canActivate: [AuthCallbackGuard], component: FrontPageComponent },
  { path: 'error', component: ErrorComponent, canActivate: [SilentAuthGuard] },
  {
    path: 'styleguide',
    loadChildren: () => import('./style-guide/style-guide.module').then(m => m.StyleGuideModule),
    canActivate: [SilentAuthGuard],
  },
  {
    path: 'skiftbruger',
    component: SkiftBrugerComponent,
    canActivate: [AuthGuard],
    canDeactivate: [CanDeactivateGuard],
  },
  {
    path: 'loggetud',
    component: LoggedOutPageComponent,
  },
  { path: '**', component: PageNotFoundComponent, canActivate: [SilentAuthGuard] },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      anchorScrolling: 'enabled',
      relativeLinkResolution: 'legacy',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule { }
